import { v4 as uuid } from 'uuid';

import { BaseStreaming } from '../Streaming';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingService from '../../abstract/StreamingService';
import {
  getMarketDepthHeader,
  getMarketDepthUnsubscribeRequest,
  MarketDepthRequestPayload,
  MarketDepthSubscribeRequest,
  MarketDepthUnsubscribeResponse,
} from './Messages';
import MarketDepthSubscription, {
  MarketDepthOnSnapshot,
  MarketDepthOnSubscribe,
  MarketDepthOnUpdate,
} from './MarketDepthSubscription';

export class MarketDepthStreaming extends StreamingService<DoryServiceType.MARKETDEPTH> {
  private available = true;

  public get isAvailable(): boolean {
    return this.available;
  }
  /**
   * @hidden
   */
  public set isAvailable(val: boolean) {
    if (val && !this.available) {
      this.available = val;
      this.resubscribe();
    } else {
      this.available = val;
    }
  }

  private subscriptions: {
    [key: string]: MarketDepthSubscription;
  } = {};

  /**
   * @hidden
   */
  constructor(socket: BaseStreaming) {
    // The service for MarketDepth is also "quote" since they both use the same
    // backend data-source
    super(DoryServiceType.MARKETDEPTH, 'quote', socket);
  }
  /**
   * Subscribe to websocket updates
   *
   * ```js example
   * dory.Streaming.connect()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function () {
   *     // Subscribe to ric
   *     const id = dory.Streaming.MarketDepth.subscribe(
   *       {
   *         rics: ['1_MD_BMWG.DE'],
   *       },
   *       // Subscribe Callback
   *       function (data, err) {
   *         if (err) {
   *           console.error(err);
   *         } else {
   *           console.log('subscribed', data);
   *         }
   *       },
   *       // Snapshot Callback
   *       function (data) {
   *         console.log('snapshot', data);
   *       },
   *       // Update Callback
   *       function (data) {
   *         console.log('update', data);
   *       }
   *     );
   *
   *     // Unsubscribe after 10 seconds
   *     window.setTimeout(function () {
   *       dory.Streaming.MarketDepth.unsubscribe(id);
   *       dory.Streaming.disconnect();
   *     }, 10000);
   *   });
   * ```
   *
   * @param data  <code>{ rics: string[] }</code>
   * @param subscribe  Executed when subscribe response is received
   * @param snapshot  Executed when snapshot is received
   * @param update  Executed when update is received
   * @param id  Unique subscription identifier
   * @returns Unique subscription identifier
   */
  public subscribe(
    data: MarketDepthRequestPayload,
    subscribe: MarketDepthOnSubscribe,
    snapshot: MarketDepthOnSnapshot,
    update: MarketDepthOnUpdate,
    id: string = uuid()
  ): string {
    const source = 'Streaming.MarketDepth.subscribe';

    const missingParams =
      subscribe === undefined || snapshot === undefined || update === undefined
        ? 'Missing required callback, "subscribe" requires callbacks for: \n- subscribe(data, error)\n- snapshot(data, error)\n- update(data, error)'
        : '';

    const errorCallback = this.getSubscribeErrorHandler(
      source,
      subscribe,
      id,
      this.subscriptions[id] !== undefined,
      missingParams
    );
    if (errorCallback) {
      return undefined;
    } else {
      // Create subscription object
      const subscription = new MarketDepthSubscription(
        id,
        this.SOCKET.EventBus,
        data.rics,
        subscribe,
        snapshot,
        update
      );
      // Store subscription
      this.subscriptions[id] = subscription;
      // Setup subscription
      subscription.start(this.SOCKET.Timeout);
      // Start subscription
      this.SOCKET.send<MarketDepthSubscribeRequest>(
        subscription.SubscribeRequest
      );
    }
    return id;
  }

  /**
   * Unsubscribe from websocket updates
   * @param id  subscription identifier
   * @param callback  Executed when unsubscribe response is received
   */
  public unsubscribe(
    id: string,
    callback?: (e: MarketDepthUnsubscribeResponse, err?: StreamingError) => void
  ): void {
    const source = 'Streaming.MarketDepth.unsubscribe';
    const subscription = this.subscriptions[id];

    if (!this.SOCKET.isConnected) {
      this.RaiseNotConnectedError(source, callback);
    } else if (!subscription) {
      if (callback) {
        callback(
          undefined,
          new StreamingError(
            source,
            StreamingErrorCodes.INVALID_ID,
            `No subscription for ID ${id} registered!`
          )
        );
      }
    } else {
      // Remove subscription from storage
      subscription.stop();
      delete this.subscriptions[id];

      this.unsubscribeRics(subscription.subscribed.rics, source, callback);
    }
  }

  /**
   * Unsubscribe from all subscriptions
   */
  public unsubscribeAll(): void {
    const ids = Object.keys(this.subscriptions);
    ids.forEach((id) => {
      this.unsubscribe(id);
    });
  }

  /**
   * @hidden
   * Resubscribe to all previous subscriptions
   */
  public resubscribe(): void {
    Object.keys(this.subscriptions).forEach((id) => {
      const subscription = this.subscriptions[id];
      subscription.restart(this.SOCKET.Timeout);
      this.SOCKET.send<MarketDepthSubscribeRequest>(
        subscription.SubscribeRequest
      );
    });
  }
  /**
   * @hidden
   *
   * In the WebSocketServer subscriptions for the same ric are merged, so we gather all rics
   * from all subscriptions and filter out the rics that are in use in other subscriptions
   */
  public unsubscribeRics(
    rics: string[],
    source: string,
    callback?: (e: MarketDepthUnsubscribeResponse, err?: StreamingError) => void
  ): void {
    const subscribedRics = [].concat(
      ...Object.keys(this.subscriptions).map(
        (id) => this.subscriptions[id].subscribed.rics
      )
    );
    // All rics not used in other subscriptions
    const unsubscribeRics = rics.filter((ric) => {
      return subscribedRics.indexOf(ric) === -1;
    });
    const header = getMarketDepthHeader(DoryMessageType.UNSUBSCRIBE);

    if (unsubscribeRics.length !== 0) {
      const msg = getMarketDepthUnsubscribeRequest({
        rics: unsubscribeRics,
      });

      if (callback) {
        this.SOCKET.EventBus.once(
          DoryServiceType.MARKETDEPTH,
          DoryMessageType.UNSUBSCRIBE,
          this.setTimeout(source, this.SOCKET.Timeout, callback)
        );
      }
      this.SOCKET.send(msg);
    } else if (callback) {
      // If there are no rics to unsubscribe, fake a successful response
      callback({
        header,
        payload: {
          status: 200,
          message: 'Successfully un-subscribed.',
        },
      });
    }
  }

  /**
   * @hidden
   * Clear all stored subscription ids
   */
  public clearSubscriptions(): void {
    this.subscriptions = {};
  }
}
